import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import StableCKEditor from '../components/StableCKEditor';
import ClassicEditor from '../utils/ckeditor';
import { renderRichHtml, toEditorHtml } from '../utils/sanitizeHtml';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  Tabs,
  Tab,
  Divider,
  Checkbox,
  FormControlLabel,
  Tooltip,
  TableSortLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Receipt as InvoiceIcon,
  Close as CloseIcon,
  Block as BlockIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  ContentCopy as CopyIcon,
  Description as DescriptionIcon,
  Visibility as ViewIcon,
  CheckCircle as CheckIcon,
  DragIndicator as DragIcon,
  CalendarMonth as CalendarIcon,
  Business as CustomerIcon,
  Download as DownloadIcon,
  ArrowDropDown as ArrowDropDownIcon,
  AutoStories as LongformIcon,
  AccessTime as TimesheetIcon,
  Preview as PreviewIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TimesheetDialog from '../components/invoices/TimesheetDialog';
import InvoicePreviewDialog from '../components/invoices/InvoicePreviewDialog';
import {
  invoicesAPI,
  customersAPI,
  projectsAPI,
} from '../services/api';
import { localISODate } from '../utils/dates';
import { useAuth } from '../contexts/AuthContext';

// Sortable Row Component for drag & drop
const SortableItemRow = ({ item, selectedInvoice, formatGerman, handleEditItem, handleDeleteItem }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? '#f5f5f5' : 'transparent',
  };

  const canEdit = ['draft', 'created', 'sent', 'paid'].includes(selectedInvoice.status);

  return (
    <TableRow ref={setNodeRef} style={style}>
      {canEdit && (
        <TableCell sx={{ width: 40, cursor: 'grab' }} {...attributes} {...listeners}>
          <DragIcon sx={{ color: 'text.secondary' }} />
        </TableCell>
      )}
      <TableCell>{item.position}</TableCell>
      <TableCell>
        <Box
          sx={{
            '& p': { margin: 0 },
            '& ul, & ol': { margin: '4px 0', paddingLeft: '20px' },
            '& li': { marginBottom: '2px' },
            '& h2, & h3, & h4': { margin: '4px 0', fontSize: '0.95rem' },
          }}
          dangerouslySetInnerHTML={{ __html: renderRichHtml(item.description) }}
        />
      </TableCell>
      <TableCell>{formatGerman(item.quantity)}</TableCell>
      <TableCell>{item.unit}</TableCell>
      <TableCell>{formatGerman(item.unit_price)} €</TableCell>
      <TableCell>
        <Typography variant="body2" fontWeight={600}>
          {formatGerman(item.total_price)} €
        </Typography>
      </TableCell>
      {canEdit && (
        <TableCell align="center">
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
            <IconButton
              size="small"
              onClick={() => handleEditItem(item)}
              sx={{
                color: '#1e3a5f',
                '&:hover': { bgcolor: 'primary.light' },
              }}
              title="Bearbeiten"
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDeleteItem(item.id)}
              sx={{
                color: '#e01e26',
                '&:hover': { bgcolor: 'error.light' },
              }}
              title="Löschen"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
};

const InvoicesPage = () => {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const handledEditRef = useRef(null);
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);
  const [mainTabValue, setMainTabValue] = useState(0); // 0 = Rechnungen, 1 = Angebote
  const [tabValue, setTabValue] = useState(0); // Status tab
  const [paidMonthFilter, setPaidMonthFilter] = useState(''); // '' = Alle Monate
  const [paidCustomerFilter, setPaidCustomerFilter] = useState(''); // '' = Alle Kunden
  const [showPaidDialog, setShowPaidDialog] = useState(false);
  const [paidDate, setPaidDate] = useState('');
  const [paidInvoiceId, setPaidInvoiceId] = useState(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [hideCancelled, setHideCancelled] = useState(true);
  const [sortField, setSortField] = useState('invoice_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // German number formatting helper
  const formatGerman = (number) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(number);
  };

  // Get available months from paid invoices for filter dropdown
  const getAvailableMonths = () => {
    if (tabValue !== 3) return [];
    const monthsSet = new Set();
    invoices.forEach(invoice => {
      if (invoice.invoice_date) {
        const date = new Date(invoice.invoice_date);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        monthsSet.add(monthKey);
      }
    });
    return Array.from(monthsSet).sort().reverse(); // Neueste zuerst
  };

  // Format month key to German label
  const formatMonthLabel = (monthKey) => {
    const [year, month] = monthKey.split('-');
    const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
                        'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    return `${monthNames[parseInt(month) - 1]} ${year}`;
  };

  // Get available customers from paid invoices for filter dropdown
  const getAvailableCustomers = () => {
    if (tabValue !== 3) return [];
    const customerMap = new Map();
    invoices.forEach(invoice => {
      if (invoice.customer_id && invoice.customer_name) {
        customerMap.set(invoice.customer_id, invoice.customer_name);
      }
    });
    return Array.from(customerMap.entries())
      .sort((a, b) => a[1].localeCompare(b[1])); // Alphabetisch sortiert
  };

  // Filter invoices by selected month and customer
  const filteredInvoices = tabValue === 3
    ? invoices.filter(invoice => {
        if (paidMonthFilter) {
          if (!invoice.invoice_date) return false;
          const date = new Date(invoice.invoice_date);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          if (monthKey !== paidMonthFilter) return false;
        }
        if (paidCustomerFilter) {
          if (invoice.customer_id !== paidCustomerFilter) return false;
        }
        if (hideCancelled) {
          if (invoice.status === 'cancelled' || invoice.cancels_invoice_id) return false;
        }
        return true;
      }).sort((a, b) => {
        let valA, valB;
        if (sortField === 'invoice_number') {
          valA = a.invoice_number || '';
          valB = b.invoice_number || '';
        } else if (sortField === 'paid_at') {
          valA = a.paid_at || '';
          valB = b.paid_at || '';
        } else {
          valA = a.invoice_date || '';
          valB = b.invoice_date || '';
        }
        const cmp = valA < valB ? -1 : valA > valB ? 1 : 0;
        return sortDirection === 'asc' ? cmp : -cmp;
      })
    : invoices;

  const [formData, setFormData] = useState({
    customer_id: '',
    project_id: '',
    invoice_type: 'invoice',
    invoice_date: localISODate(),
    service_date: '',
    due_date_days: '14',
    validity_days: '30',
    subject: '',
    discount_rate: '0',
    discount_days: '0',
    tax_rate: '19',
    intro_text: '',
    notes: '',
    claim: 'drupal',
    doc_variant: 'offer',
  });

  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showItemsDialog, setShowItemsDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [timesheetInvoice, setTimesheetInvoice] = useState(null);
  const [preview, setPreview] = useState(null); // { invoice, tab }
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [itemFormData, setItemFormData] = useState({
    article_number: '',
    description: '',
    quantity: '',
    unit: 'Stunden',
    unit_price: '',
  });
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailFormData, setEmailFormData] = useState({
    recipient: '',
    cc: '',
    subject: '',
    body: '',
    footer: '',
    invoiceId: null,
    isCorrection: false,
  });

  const { user } = useAuth();

  // Drag & drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchData();
    setSelectedInvoiceIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, mainTabValue]);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      const statusMap = ['draft', 'created', 'sent', 'paid'];
      const status = tabValue === 3 ? 'paid,cancelled' : statusMap[tabValue];
      const invoiceType = mainTabValue === 0 ? 'invoice' : 'quote';

      const [invoicesResponse, customersResponse, projectsResponse] = await Promise.all([
        invoicesAPI.getAll({ status, type: invoiceType }),
        customersAPI.getAll(false),
        projectsAPI.getAll(false),
      ]);

      setInvoices(invoicesResponse.data);
      setCustomers(customersResponse.data);
      setProjects(projectsResponse.data);
    } catch (err) {
      setError('Fehler beim Laden der Daten');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const data = {
        ...formData,
        project_id: formData.project_id || null,
        service_date: formData.service_date || null,
        subject: formData.subject || null,
        intro_text: formData.intro_text || null,
        notes: formData.notes || null,
      };

      const docLabel = formData.invoice_type !== 'quote' ? 'Rechnung'
        : formData.doc_variant === 'sketch' ? 'Projektskizze' : 'Angebot';

      if (editingId) {
        await invoicesAPI.update(editingId, data);
        setSuccess(`${docLabel} erfolgreich aktualisiert`);
      } else {
        const created = await invoicesAPI.create(data);
        // Longform wird sofort geschrieben -- direkt in den Editor, statt den
        // Nutzer erst in der Liste nach dem neuen Angebot suchen zu lassen.
        if (created.data?.layout === 'longform') {
          resetForm();
          setShowModal(false);
          navigate(`/invoices/longform/${created.data.id}`);
          return;
        }
        setSuccess(`${docLabel} erfolgreich erstellt`);
        setTabValue(0); // Go to Entwürfe tab to see new invoice
      }

      resetForm();
      setShowModal(false);
      if (!editingId && tabValue !== 0) {
        // New invoice: tab changes to Entwürfe, useEffect handles fetch
      } else {
        // Edit (same tab) or new invoice while already on Entwürfe tab
        fetchData();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Speichern des Dokuments');
    }
  };

  const handleEdit = (invoice) => {
    // Auch bei Longform führt der Stift in die Kopfdaten (Kunde, Betreff,
    // Gültigkeit, Steuersatz, Tagessatz, Deckblatt-Titel, AGB) -- den Inhalt
    // bearbeitet das Auge im Baustein-Editor. Gleiche Aufteilung wie bei der
    // Kurzform, wo der Stift die Kopfdaten und das Auge die Positionen öffnet.
    // Calculate validity_days if valid_until is set
    let validityDays = '30';
    if (invoice.valid_until && invoice.invoice_date) {
      const validUntil = new Date(invoice.valid_until);
      const invoiceDate = new Date(invoice.invoice_date);
      const diffTime = validUntil - invoiceDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      validityDays = diffDays.toString();
    }

    setFormData({
      customer_id: invoice.customer_id,
      project_id: invoice.project_id || '',
      invoice_type: invoice.invoice_type,
      invoice_date: invoice.invoice_date,
      service_date: invoice.service_date || '',
      due_date_days: invoice.due_date_days ? invoice.due_date_days.toString() : '14',
      validity_days: validityDays,
      subject: invoice.subject || '',
      discount_rate: invoice.discount_rate ? invoice.discount_rate.toString() : '0',
      discount_days: invoice.discount_days ? invoice.discount_days.toString() : '0',
      tax_rate: invoice.tax_rate.toString(),
      intro_text: invoice.intro_text || '',
      notes: invoice.notes || '',
      layout: invoice.layout || 'compact',
      claim: invoice.claim || 'drupal',
      doc_variant: invoice.doc_variant || 'offer',
      cover_title: invoice.cover_title || '',
      daily_rate: invoice.daily_rate != null ? invoice.daily_rate.toString() : '',
      attach_terms: invoice.attach_terms !== false,
    });
    setEditingId(invoice.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Möchten Sie diese Rechnung wirklich löschen?')) {
      return;
    }

    try {
      await invoicesAPI.delete(id);
      setSuccess('Rechnung erfolgreich gelöscht');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Löschen der Rechnung');
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await invoicesAPI.duplicate(id);
      setSuccess('Rechnung erfolgreich dupliziert');
      if (tabValue === 0) {
        fetchData(); // Already on Entwürfe tab, just refresh
      } else {
        setTabValue(0); // Go to Entwürfe tab to see the copy
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Duplizieren der Rechnung');
    }
  };

  const handleFinalize = async (id) => {
    if (!window.confirm('Möchten Sie diese Rechnung wirklich finalisieren? Die Rechnungsnummer wird vergeben und das PDF wird erstellt.')) {
      return;
    }

    try {
      await invoicesAPI.finalize(id);
      setSuccess('Rechnung erfolgreich finalisiert');
      setTabValue(1); // Go to Erstellt tab
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Finalisieren der Rechnung');
    }
  };

  const handleMarkPaid = (id) => {
    setPaidInvoiceId(id);
    setPaidDate(localISODate());
    setShowPaidDialog(true);
  };

  const handleConfirmPaid = async () => {
    try {
      await invoicesAPI.markPaid(paidInvoiceId, { paid_at: paidDate });
      setSuccess('Rechnung als bezahlt markiert');
      setShowPaidDialog(false);
      setPaidInvoiceId(null);
      setTabValue(3); // Go to Bezahlt tab
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Markieren der Rechnung');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedInvoiceIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const handleToggleSelectAll = () => {
    if (selectedInvoiceIds.length === filteredInvoices.length) {
      setSelectedInvoiceIds([]);
    } else {
      setSelectedInvoiceIds(filteredInvoices.map((inv) => inv.id));
    }
  };

  const handleDownloadZip = async () => {
    try {
      const response = await invoicesAPI.downloadZip(selectedInvoiceIds);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Rechnungen_${localISODate().replace(/-/g, '')}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Fehler beim Erstellen des ZIP-Downloads');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Möchten Sie diese Rechnung wirklich stornieren? Es wird eine Gutschrift erstellt.')) {
      return;
    }

    try {
      await invoicesAPI.cancel(id);
      setSuccess('Rechnung erfolgreich storniert. Gutschrift wurde erstellt.');
      setTabValue(3); // Go to Bezahlt tab
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Stornieren der Rechnung');
    }
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await invoicesAPI.downloadPDF(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from Content-Disposition header or use fallback
      const contentDisposition = response.headers['content-disposition'];
      let filename = `rechnung_${id}.pdf`; // fallback
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Fehler beim Download des PDFs');
    }
  };

  const handleSendEmail = async (invoiceId) => {
    try {
      // Den Vorschlagstext liefert der Server -- so wird er nur an einer Stelle
      // gepflegt und stimmt mit dem überein, was ohne Anpassung versendet würde.
      const [invoiceRes, draftRes] = await Promise.all([
        invoicesAPI.getById(invoiceId),
        invoicesAPI.getEmailDraft(invoiceId),
      ]);
      const invoice = invoiceRes.data;
      const draft = draftRes.data;

      if (!draft.recipient) {
        setError('Kunde hat keine E-Mail-Adresse hinterlegt');
        return;
      }

      setSelectedInvoice(invoice);
      setEmailFormData({
        invoiceId,                       // handleEmailSubmit sendet an diese ID
        // Steuert Erfolgsmeldung und Tabwechsel: eine erneut versendete
        // Rechnung ist eine Korrektur und wechselt den Status nicht.
        isCorrection: invoice.invoice_type === 'invoice'
          && ['sent', 'paid'].includes(invoice.status),
        recipient: draft.recipient,
        cc: draft.cc || '',
        subject: draft.subject,
        body: draft.body,
        footer: draft.footer,
      });
      setShowEmailDialog(true);
    } catch (err) {
      setError('Fehler beim Laden der Rechnungsdaten');
    }
  };

  const handleEmailChange = (e) => {
    setEmailFormData({
      ...emailFormData,
      [e.target.name]: e.target.value,
    });
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await invoicesAPI.sendEmail(emailFormData.invoiceId, {
        recipient: emailFormData.recipient,
        cc: emailFormData.cc || null,
        subject: emailFormData.subject,
        body: emailFormData.body,
        footer: emailFormData.footer,
      });
      setShowEmailDialog(false);
      if (emailFormData.isCorrection) {
        setSuccess('Korrektur erfolgreich versendet');
        fetchData(); // Stay on current tab, refresh to show updated correction_count
      } else {
        setSuccess(`${selectedInvoice?.invoice_type === 'quote' ? 'Angebot' : 'Rechnung'} erfolgreich per E-Mail versendet`);
        setTabValue(2); // Go to Versendet tab
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Versenden der E-Mail');
    }
  };

  const handleConvertToInvoice = async (id) => {
    if (!window.confirm('Möchten Sie dieses Angebot in eine Rechnung umwandeln?')) {
      return;
    }

    try {
      await invoicesAPI.convertToInvoice(id);
      setSuccess('Angebot erfolgreich in Rechnung umgewandelt');
      setMainTabValue(0); // Switch to Rechnungen
      setTabValue(0); // Go to Entwürfe tab to see the new invoice
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Umwandeln des Angebots');
    }
  };

  const handleViewItems = async (invoice) => {
    // Longform-Angebote haben keinen Positionsdialog, sondern einen eigenen Editor.
    if (invoice.layout === 'longform') {
      navigate(`/invoices/longform/${invoice.id}`);
      return;
    }
    try {
      const response = await invoicesAPI.getById(invoice.id);
      setSelectedInvoice(response.data);
      setItems(response.data.items || []);
      setShowItemsDialog(true);
    } catch (err) {
      setError('Fehler beim Laden der Positionen');
    }
  };

  const handleAddItem = () => {
    // Get customer's hourly rate as default
    let defaultUnitPrice = '';
    if (selectedInvoice) {
      const customer = customers.find(c => c.id === selectedInvoice.customer_id);
      if (customer && customer.hourly_rate) {
        defaultUnitPrice = customer.hourly_rate.toString();
      }
    }

    setEditingItemId(null);
    setItemFormData({
      article_number: '',
      description: '',
      quantity: '',
      unit: 'Stunden',
      unit_price: defaultUnitPrice,
    });
    setShowAddItemDialog(true);
  };

  const handleEditItem = (item) => {
    setEditingItemId(item.id);
    setItemFormData({
      article_number: item.article_number || '',
      description: item.description,
      quantity: item.quantity.toString(),
      unit: item.unit,
      unit_price: item.unit_price.toString(),
    });
    setShowAddItemDialog(true);
  };

  const handleItemChange = (e) => {
    const { name, value } = e.target;
    const newFormData = {
      ...itemFormData,
      [name]: value,
    };

    // Auto-fill unit_price based on unit selection
    if (name === 'unit' && selectedInvoice) {
      const customer = customers.find(c => c.id === selectedInvoice.customer_id);
      if (customer) {
        if (value === 'Stunden' && customer.hourly_rate) {
          newFormData.unit_price = customer.hourly_rate.toString();
        } else if (value === 'PT' && customer.daily_rate) {
          newFormData.unit_price = customer.daily_rate.toString();
        }
      }
    }

    setItemFormData(newFormData);
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingItemId) {
        // Update existing item
        await invoicesAPI.updateItem(selectedInvoice.id, editingItemId, itemFormData);
        setSuccess('Position erfolgreich aktualisiert');
      } else {
        // Add new item
        await invoicesAPI.addItem(selectedInvoice.id, itemFormData);
        setSuccess('Position erfolgreich hinzugefügt');
      }

      // Reload invoice items
      const response = await invoicesAPI.getById(selectedInvoice.id);
      setSelectedInvoice(response.data);
      setItems(response.data.items || []);

      setShowAddItemDialog(false);
      setEditingItemId(null);
      setItemFormData({
        article_number: '',
        description: '',
        quantity: '',
        unit: 'Stunden',
        unit_price: '',
      });
    } catch (err) {
      setError(err.response?.data?.error || (editingItemId ? 'Fehler beim Aktualisieren der Position' : 'Fehler beim Hinzufügen der Position'));
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Möchten Sie diese Position wirklich löschen?')) {
      return;
    }

    try {
      await invoicesAPI.deleteItem(selectedInvoice.id, itemId);
      setSuccess('Position erfolgreich gelöscht');

      // Reload invoice items
      const response = await invoicesAPI.getById(selectedInvoice.id);
      setSelectedInvoice(response.data);
      setItems(response.data.items || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Löschen der Position');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    // Optimistically update UI
    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);

    // Update positions in backend
    try {
      const positionUpdates = newItems.map((item, index) => ({
        id: item.id,
        position: index + 1,
      }));

      await invoicesAPI.reorderItems(selectedInvoice.id, positionUpdates);

      // Reload to get fresh data with updated totals
      const response = await invoicesAPI.getById(selectedInvoice.id);
      setSelectedInvoice(response.data);
      setItems(response.data.items || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Sortieren der Positionen');
      // Reload original order on error
      const response = await invoicesAPI.getById(selectedInvoice.id);
      setSelectedInvoice(response.data);
      setItems(response.data.items || []);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      project_id: '',
      invoice_type: 'invoice',
      invoice_date: localISODate(),
      service_date: '',
      due_date_days: '14',
      validity_days: '30',
      subject: '',
      discount_rate: '0',
      discount_days: '0',
      tax_rate: '19',
      intro_text: '',
      notes: '',
      layout: 'compact',
      claim: 'drupal',
      doc_variant: 'offer',
    });
    setEditingId(null);
  };

  // Der Editor schickt den Nutzer mit `editInvoiceId` hierher, wenn er die
  // Kopfdaten aufrufen will. Sobald die Liste geladen ist, öffnen wir den Dialog
  // und räumen den State weg, damit ein Reload ihn nicht erneut aufklappt.
  useEffect(() => {
    const wanted = location.state?.editInvoiceId;
    if (!wanted || handledEditRef.current === wanted) return;
    // Merken statt den Navigations-State sofort zu leeren: das Leeren ändert
    // die Dependency, würde den Effekt neu auslösen und die noch laufende
    // Anfrage verwerfen.
    handledEditRef.current = wanted;
    // Gezielt laden statt in der Liste suchen: die ist nach Haupt- und
    // Status-Tab gefiltert, das gesuchte Angebot muss dort nicht enthalten sein.
    (async () => {
      try {
        const res = await invoicesAPI.getById(wanted);
        const invoice = res.data;
        // In den passenden Tab wechseln, damit die Liste hinter dem Dialog das
        // Dokument auch zeigt.
        setMainTabValue(invoice.invoice_type === 'quote' ? 1 : 0);
        handleEdit(invoice);
      } catch (err) {
        setError('Angebot konnte nicht geladen werden');
      } finally {
        // Erst jetzt den State leeren -- die Ref oben verhindert, dass der
        // dadurch neu laufende Effekt noch etwas anstößt. Ohne das Leeren
        // klappt der Dialog nach einem Reload erneut auf.
        navigate(location.pathname, { replace: true, state: {} });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.editInvoiceId]);

  // Vom Dashboard („Offene Rechnungen"): direkt in den Status-Tab springen
  useEffect(() => {
    const wanted = location.state?.statusTab;
    if (wanted == null) return;
    setMainTabValue(0);
    setTabValue(wanted);
    navigate(location.pathname, { replace: true, state: {} });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.statusTab]);

  const openCreateModal = (layout = 'compact', docVariant = 'offer') => {
    resetForm();
    // Set invoice_type based on current main tab
    setFormData(prev => ({
      ...prev,
      invoice_type: mainTabValue === 0 ? 'invoice' : 'quote',
      layout,
      doc_variant: docVariant,
      // Projektskizze ist unverbindlich -- AGB gehören nicht dazu.
      attach_terms: docVariant !== 'sketch',
    }));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const closeItemsDialog = () => {
    setShowItemsDialog(false);
    setSelectedInvoice(null);
    setItems([]);
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer ? customer.company_name : '-';
  };

  const getProjectName = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    return project ? project.name : '-';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return { bgcolor: '#fff3e0', color: '#f57c00' };
      case 'created':
        return { bgcolor: '#e1f5fe', color: '#0277bd' };
      case 'sent':
        return { bgcolor: '#e3f2fd', color: '#1976d2' };
      case 'paid':
        return { bgcolor: '#e8f5e9', color: '#388e3c' };
      case 'cancelled':
        return { bgcolor: '#ffebee', color: '#d32f2f' };
      default:
        return { bgcolor: '#f5f5f5', color: '#616161' };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'draft':
        return 'Entwurf';
      case 'created':
        return 'Erstellt';
      case 'sent':
        return 'Versendet';
      case 'paid':
        return 'Bezahlt';
      case 'cancelled':
        return 'Storniert';
      default:
        return status;
    }
  };

  if (user.role !== 'admin') {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              <BlockIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3, color: '#e01e26' }} />
              <Typography variant="h5" fontWeight={600} gutterBottom>
                Zugriff verweigert
              </Typography>
              <Typography variant="body1" color="error.main">
                Sie haben keine Berechtigung, auf diese Seite zuzugreifen. Admin-Rechte erforderlich.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress size={60} sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Rechnungsverwaltung
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Verwalten Sie Rechnungen und Angebote
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          endIcon={mainTabValue === 1 ? <ArrowDropDownIcon /> : null}
          onClick={(e) => {
            // Angebote gibt es in zwei Formen -- die Wahl steht direkt im Button.
            if (mainTabValue === 1) setCreateMenuAnchor(e.currentTarget);
            else openCreateModal('compact');
          }}
          sx={{
            background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
            color: 'white',
            fontWeight: 600,
            px: 3,
            py: 1,
            '&:hover': {
              background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
            },
          }}
        >
          {mainTabValue === 0 ? 'Rechnung erstellen' : 'Angebot erstellen'}
        </Button>
        <Menu
          anchorEl={createMenuAnchor}
          open={Boolean(createMenuAnchor)}
          onClose={() => setCreateMenuAnchor(null)}
        >
          <MenuItem onClick={() => { setCreateMenuAnchor(null); openCreateModal('compact'); }}>
            <Box>
              <Typography variant="body2" fontWeight={600}>Kurzform</Typography>
              <Typography variant="caption" color="text.secondary">
                Kalkulation auf dem Briefbogen
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => { setCreateMenuAnchor(null); openCreateModal('longform'); }}>
            <Box>
              <Typography variant="body2" fontWeight={600}>Longform</Typography>
              <Typography variant="caption" color="text.secondary">
                Dokument mit Deckblatt, Prosa und AGB
              </Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => { setCreateMenuAnchor(null); openCreateModal('longform', 'sketch'); }}>
            <Box>
              <Typography variant="body2" fontWeight={600}>Projektskizze</Typography>
              <Typography variant="caption" color="text.secondary">
                Unverbindliche Einschätzung vor dem Angebot, ohne AGB
              </Typography>
            </Box>
          </MenuItem>
        </Menu>
      </Box>

      {success && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main Tabs - Rechnungen vs Angebote */}
      <Card sx={{ borderRadius: 3, mb: 3 }}>
        <Tabs
          value={mainTabValue}
          onChange={(e, newValue) => setMainTabValue(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root': {
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '1.1rem',
              minHeight: '60px',
            },
          }}
        >
          <Tab label="Rechnungen" icon={<InvoiceIcon />} iconPosition="start" />
          <Tab label="Angebote" icon={<DescriptionIcon />} iconPosition="start" />
        </Tabs>
      </Card>

      {/* Status Tabs */}
      <Card sx={{ borderRadius: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
            },
          }}
        >
          <Tab label="Entwürfe" />
          <Tab label="Erstellt" />
          <Tab label="Versendet" />
          {mainTabValue === 0 && <Tab label="Bezahlt" />}
        </Tabs>

        <CardContent>
          {/* Month Filter for Paid Tab */}
          {tabValue === 3 && mainTabValue === 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CalendarIcon sx={{ color: 'text.secondary' }} />
              <TextField
                select
                size="small"
                label="Monat filtern"
                value={paidMonthFilter}
                onChange={(e) => { setPaidMonthFilter(e.target.value); setSelectedInvoiceIds([]); }}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">
                  <em>Alle Monate</em>
                </MenuItem>
                {getAvailableMonths().map((monthKey) => (
                  <MenuItem key={monthKey} value={monthKey}>
                    {formatMonthLabel(monthKey)}
                  </MenuItem>
                ))}
              </TextField>
              <CustomerIcon sx={{ color: 'text.secondary', ml: 1 }} />
              <TextField
                select
                size="small"
                label="Kunde filtern"
                value={paidCustomerFilter}
                onChange={(e) => { setPaidCustomerFilter(e.target.value); setSelectedInvoiceIds([]); }}
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">
                  <em>Alle Kunden</em>
                </MenuItem>
                {getAvailableCustomers().map(([customerId, customerName]) => (
                  <MenuItem key={customerId} value={customerId}>
                    {customerName}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
                <Checkbox
                  size="small"
                  checked={hideCancelled}
                  onChange={(e) => { setHideCancelled(e.target.checked); setSelectedInvoiceIds([]); }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  Stornierte ausblenden
                </Typography>
              </Box>
              {(paidMonthFilter || paidCustomerFilter || hideCancelled) && (
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                  {filteredInvoices.length} von {invoices.length} Rechnungen
                </Typography>
              )}
            </Box>
          )}

          {filteredInvoices.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 8,
                color: 'text.secondary',
              }}
            >
              {mainTabValue === 0 ? (
                <InvoiceIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              ) : (
                <DescriptionIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
              )}
              <Typography variant="h6" gutterBottom>
                {mainTabValue === 0 ? 'Keine Rechnungen vorhanden' : 'Keine Angebote vorhanden'}
              </Typography>
              <Typography variant="body2">
                {mainTabValue === 0 ? 'Erstellen Sie Ihre erste Rechnung!' : 'Erstellen Sie Ihr erstes Angebot!'}
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mt: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {tabValue === 3 ? (
                        <TableSortLabel active={sortField === 'invoice_number'} direction={sortField === 'invoice_number' ? sortDirection : 'desc'} onClick={() => handleSort('invoice_number')}>
                          Rechnungs-Nr.
                        </TableSortLabel>
                      ) : 'Rechnungs-Nr.'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Typ</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Kunde</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Projekt</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {tabValue === 3 ? (
                        <TableSortLabel active={sortField === 'invoice_date'} direction={sortField === 'invoice_date' ? sortDirection : 'desc'} onClick={() => handleSort('invoice_date')}>
                          Datum
                        </TableSortLabel>
                      ) : 'Datum'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Betrag</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    {tabValue === 3 && (
                      <TableCell sx={{ fontWeight: 600 }}>
                        <TableSortLabel active={sortField === 'paid_at'} direction={sortField === 'paid_at' ? sortDirection : 'desc'} onClick={() => handleSort('paid_at')}>
                          Bezahlt am
                        </TableSortLabel>
                      </TableCell>
                    )}
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Aktionen</TableCell>
                    {tabValue === 3 && (
                      <TableCell align="center" sx={{ fontWeight: 600 }}>
                        <Checkbox
                          size="small"
                          checked={filteredInvoices.length > 0 && selectedInvoiceIds.length === filteredInvoices.length}
                          indeterminate={selectedInvoiceIds.length > 0 && selectedInvoiceIds.length < filteredInvoices.length}
                          onChange={handleToggleSelectAll}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredInvoices.map((invoice) => (
                    <TableRow
                      key={invoice.id}
                      sx={{
                        '&:hover': {
                          bgcolor: invoice.status === 'cancelled' ? '#ffebee' : 'action.hover',
                        },
                        ...(invoice.status === 'cancelled' && { bgcolor: '#fff5f5' }),
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                          {invoice.invoice_number}
                        </Typography>
                        {invoice.correction_count > 0 && (
                          <Chip
                            label={`Korrigiert${invoice.correction_count > 1 ? ` (${invoice.correction_count}×)` : ''}${invoice.corrected_at ? ' · ' + new Date(invoice.corrected_at).toLocaleDateString('de-DE') : ''}`}
                            size="small"
                            sx={{
                              mt: 0.5,
                              bgcolor: '#fff3e0',
                              color: '#e65100',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: 20,
                            }}
                            title={invoice.corrected_at ? `Zuletzt korrigiert am ${new Date(invoice.corrected_at).toLocaleString('de-DE')}` : ''}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            invoice.status === 'cancelled' ? 'Storniert' :
                            invoice.cancels_invoice_id ? 'Gutschrift' :
                            invoice.invoice_type === 'invoice' ? 'Rechnung' :
                            invoice.doc_variant === 'sketch' ? 'Projektskizze' : 'Angebot'
                          }
                          size="small"
                          sx={{
                            bgcolor: invoice.status === 'cancelled' ? '#ffebee' :
                                     invoice.cancels_invoice_id ? '#f3e5f5' :
                                     invoice.invoice_type === 'invoice' ? '#e8f5e9' :
                                     invoice.doc_variant === 'sketch' ? '#e3f2fd' : '#fff3e0',
                            color: invoice.status === 'cancelled' ? '#d32f2f' :
                                   invoice.cancels_invoice_id ? '#7b1fa2' :
                                   invoice.invoice_type === 'invoice' ? '#388e3c' :
                                   invoice.doc_variant === 'sketch' ? '#1565c0' : '#f57c00',
                            fontWeight: 600,
                          }}
                        />
                        {invoice.layout === 'longform' && (
                          <Tooltip title="Longform: Dokument mit Deckblatt und Prosa">
                            <Chip
                              icon={<LongformIcon sx={{ fontSize: 14 }} />}
                              label="Longform"
                              size="small"
                              variant="outlined"
                              sx={{ ml: 0.5, fontWeight: 600 }}
                            />
                          </Tooltip>
                        )}
                        {invoice.claim === 'ai' && (
                          <Tooltip title="Claim: AI Superheroes at your Service.">
                            <Chip label="AI" size="small" variant="outlined"
                              sx={{ ml: 0.5, fontWeight: 600 }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getCustomerName(invoice.customer_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {getProjectName(invoice.project_id)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(invoice.invoice_date).toLocaleDateString('de-DE')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            ...(invoice.status === 'cancelled' && { textDecoration: 'line-through', color: '#d32f2f' }),
                            ...(invoice.cancels_invoice_id && invoice.total_gross < 0 && { color: '#d32f2f' }),
                          }}
                        >
                          {formatGerman(invoice.total_gross)} €
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(invoice.status)}
                          size="small"
                          sx={{
                            ...getStatusColor(invoice.status),
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      {tabValue === 3 && (
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {invoice.paid_at
                              ? new Date(invoice.paid_at).toLocaleDateString('de-DE')
                              : '–'}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleViewItems(invoice)}
                            sx={{ color: '#616161', '&:hover': { bgcolor: 'action.hover' } }}
                            title="Positionen anzeigen"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setPreview({ invoice, tab: 'invoice' })}
                            sx={{ color: '#616161', '&:hover': { bgcolor: 'action.hover' } }}
                            title="PDF-Vorschau"
                          >
                            <PreviewIcon fontSize="small" />
                          </IconButton>
                          {invoice.invoice_type === 'invoice' && (
                            <IconButton
                              size="small"
                              onClick={() => setTimesheetInvoice(invoice)}
                              sx={{
                                color: invoice.has_timesheet ? '#e01e26' : '#bdbdbd',
                                '&:hover': { bgcolor: 'action.hover' },
                              }}
                              title={invoice.has_timesheet ? 'Zeitnachweis angehängt – ändern' : 'Zeitnachweis anhängen'}
                            >
                              <TimesheetIcon fontSize="small" />
                            </IconButton>
                          )}

                          {invoice.status === 'draft' && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(invoice)}
                                sx={{ color: '#1e3a5f', '&:hover': { bgcolor: 'primary.light' } }}
                                title="Bearbeiten"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleFinalize(invoice.id)}
                                sx={{ color: '#388e3c', '&:hover': { bgcolor: 'success.light' } }}
                                title="Finalisieren"
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}

                          {invoice.status === 'created' && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(invoice)}
                                sx={{ color: '#1e3a5f', '&:hover': { bgcolor: 'primary.light' } }}
                                title="Bearbeiten"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadPDF(invoice.id)}
                                sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'error.light' } }}
                                title="PDF herunterladen"
                              >
                                <PdfIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleSendEmail(invoice.id)}
                                sx={{ color: '#1976d2', '&:hover': { bgcolor: 'info.light' } }}
                                title="Per E-Mail senden"
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(invoice.id)}
                                sx={{ color: '#e01e26', '&:hover': { bgcolor: 'error.light' } }}
                                title="Löschen"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}

                          {invoice.status === 'sent' && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(invoice)}
                                sx={{ color: '#1e3a5f', '&:hover': { bgcolor: 'primary.light' } }}
                                title="Korrigieren"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadPDF(invoice.id)}
                                sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'error.light' } }}
                                title="PDF herunterladen"
                              >
                                <PdfIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleSendEmail(invoice.id)}
                                sx={{ color: '#1976d2', '&:hover': { bgcolor: 'info.light' } }}
                                title="Per E-Mail senden / Korrektur senden"
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                              {invoice.invoice_type === 'invoice' && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleMarkPaid(invoice.id)}
                                  sx={{ color: '#388e3c', '&:hover': { bgcolor: 'success.light' } }}
                                  title="Als bezahlt markieren"
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              )}
                              {invoice.invoice_type === 'invoice' && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleCancel(invoice.id)}
                                  sx={{ color: '#d32f2f', '&:hover': { bgcolor: '#ffebee' } }}
                                  title="Stornieren (Gutschrift erstellen)"
                                >
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              )}
                              {invoice.invoice_type === 'quote' && invoice.doc_variant !== 'sketch' && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleConvertToInvoice(invoice.id)}
                                  sx={{ color: '#7b1fa2', '&:hover': { bgcolor: 'secondary.light' } }}
                                  title="In Rechnung umwandeln"
                                >
                                  <InvoiceIcon fontSize="small" />
                                </IconButton>
                              )}
                            </>
                          )}

                          {invoice.status === 'paid' && !invoice.cancels_invoice_id && (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(invoice)}
                                sx={{ color: '#1e3a5f', '&:hover': { bgcolor: 'primary.light' } }}
                                title="Korrigieren"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDownloadPDF(invoice.id)}
                                sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'error.light' } }}
                                title="PDF herunterladen"
                              >
                                <PdfIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleSendEmail(invoice.id)}
                                sx={{ color: '#1976d2', '&:hover': { bgcolor: 'info.light' } }}
                                title="Korrektur senden"
                              >
                                <EmailIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}

                          {((invoice.status === 'paid' && invoice.cancels_invoice_id) || invoice.status === 'cancelled') && (
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadPDF(invoice.id)}
                              sx={{ color: '#d32f2f', '&:hover': { bgcolor: 'error.light' } }}
                              title="PDF herunterladen"
                            >
                              <PdfIcon fontSize="small" />
                            </IconButton>
                          )}

                          <IconButton
                            size="small"
                            onClick={() => handleDuplicate(invoice.id)}
                            sx={{ color: '#f57c00', '&:hover': { bgcolor: 'warning.light' } }}
                            title="Duplizieren"
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>

                          {invoice.status === 'draft' && (
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(invoice.id)}
                              sx={{ color: '#e01e26', '&:hover': { bgcolor: 'error.light' } }}
                              title="Löschen"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}

                          {invoice.invoice_type === 'quote' && invoice.status === 'draft' && invoice.doc_variant !== 'sketch' && (
                            <IconButton
                              size="small"
                              onClick={() => handleConvertToInvoice(invoice.id)}
                              sx={{ color: '#7b1fa2', '&:hover': { bgcolor: 'secondary.light' } }}
                              title="In Rechnung umwandeln"
                            >
                              <DescriptionIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                      {tabValue === 3 && (
                        <TableCell align="center">
                          <Checkbox
                            size="small"
                            checked={selectedInvoiceIds.includes(invoice.id)}
                            onChange={() => handleToggleSelect(invoice.id)}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Total Sum for Sent and Paid invoices */}
          {filteredInvoices.length > 0 && (tabValue === 2 || tabValue === 3) && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: 'grey.50',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" fontWeight={600}>
                  {tabValue === 2
                    ? 'Summe offener Rechnungen:'
                    : paidMonthFilter
                      ? `Summe ${formatMonthLabel(paidMonthFilter)}:`
                      : 'Summe bezahlter Rechnungen:'}
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ color: tabValue === 2 ? '#1976d2' : '#388e3c' }}>
                  {formatGerman(filteredInvoices.reduce((sum, invoice) => sum + parseFloat(invoice.total_gross || 0), 0))} €
                </Typography>
              </Box>
            </Box>
          )}

          {/* ZIP Download Button */}
          {tabValue === 3 && selectedInvoiceIds.length > 0 && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadZip}
                sx={{ textTransform: 'none' }}
              >
                {selectedInvoiceIds.length} {selectedInvoiceIds.length === 1 ? 'Rechnung' : 'Rechnungen'} als ZIP herunterladen
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Modal */}
      <Dialog
        open={showModal}
        onClose={closeModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
            {`${formData.invoice_type !== 'quote' ? 'Rechnung'
              : formData.doc_variant === 'sketch' ? 'Projektskizze' : 'Angebot'} ${editingId ? 'bearbeiten' : 'erstellen'}`}
          </Box>
          <IconButton onClick={closeModal} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="invoice-form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                select
                fullWidth
                margin="normal"
                label="Kunde"
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                required
              >
                {customers.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.company_name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                margin="normal"
                label="Projekt (optional)"
                name="project_id"
                value={formData.project_id}
                onChange={handleChange}
              >
                <MenuItem value="">
                  <em>Kein Projekt</em>
                </MenuItem>
                {projects.map((project) => (
                  <MenuItem key={project.id} value={project.id}>
                    {project.name}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                margin="normal"
                label="Typ"
                name="invoice_type"
                value={formData.invoice_type}
                onChange={handleChange}
                required
              >
                <MenuItem value="invoice">Rechnung</MenuItem>
                <MenuItem value="quote">Angebot</MenuItem>
              </TextField>

              <TextField
                select
                fullWidth
                margin="normal"
                label="Steuersatz"
                name="tax_rate"
                value={formData.tax_rate}
                onChange={handleChange}
                required
              >
                <MenuItem value="0">0% (Steuerfrei)</MenuItem>
                <MenuItem value="7">7% (Ermäßigt)</MenuItem>
                <MenuItem value="19">19% (Normal)</MenuItem>
              </TextField>

              <TextField
                fullWidth
                margin="normal"
                label="Rechnungsdatum"
                name="invoice_date"
                type="date"
                value={formData.invoice_date}
                onChange={handleChange}
                required
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                margin="normal"
                label="Leistungsdatum (optional)"
                name="service_date"
                type="date"
                value={formData.service_date}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                helperText="Standard: Rechnungsdatum"
              />

              {formData.invoice_type === 'invoice' ? (
                <TextField
                  select
                  fullWidth
                  margin="normal"
                  label="Fälligkeitszeitraum"
                  name="due_date_days"
                  value={formData.due_date_days}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="14">14 Tage</MenuItem>
                  <MenuItem value="30">30 Tage</MenuItem>
                </TextField>
              ) : (
                <TextField
                  select
                  fullWidth
                  margin="normal"
                  label="Gültigkeitszeitraum"
                  name="validity_days"
                  value={formData.validity_days}
                  onChange={handleChange}
                  required
                >
                  <MenuItem value="14">14 Tage</MenuItem>
                  <MenuItem value="30">30 Tage</MenuItem>
                  <MenuItem value="60">60 Tage</MenuItem>
                  <MenuItem value="90">90 Tage</MenuItem>
                </TextField>
              )}

              <TextField
                select
                fullWidth
                margin="normal"
                label="Claim"
                name="claim"
                value={formData.claim || 'drupal'}
                onChange={handleChange}
                helperText="Zusatz unter dem Logo im PDF"
              >
                <MenuItem value="drupal">Drupal Superheroes at your Service.</MenuItem>
                <MenuItem value="ai">AI Superheroes at your Service.</MenuItem>
              </TextField>
            </Box>

            <TextField
              fullWidth
              margin="normal"
              label="Betreff (optional)"
              name="subject"
              value={formData.subject}
              onChange={handleChange}
              placeholder="z.B. Beratungsleistung Januar 2025"
            />

            {formData.layout === 'longform' && (
              <>
                <TextField
                  select
                  fullWidth
                  margin="normal"
                  label="Dokumentart"
                  name="doc_variant"
                  value={formData.doc_variant || 'offer'}
                  onChange={handleChange}
                  helperText={formData.doc_variant === 'sketch'
                    ? 'Unverbindliche Schätzung: ohne MwSt.-Summe, Gültigkeit und AGB'
                    : 'Verbindliches Angebot mit Gesamtkalkulation'}
                >
                  <MenuItem value="offer">Angebot</MenuItem>
                  <MenuItem value="sketch">Projektskizze</MenuItem>
                </TextField>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Titel auf dem Deckblatt"
                  name="cover_title"
                  value={formData.cover_title || ''}
                  onChange={handleChange}
                  placeholder="z.B. Relaunch www.kunde.de"
                  helperText="Ohne Angabe wird der Betreff verwendet"
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    margin="normal"
                    type="number"
                    label="Tagessatz (€)"
                    name="daily_rate"
                    value={formData.daily_rate || ''}
                    onChange={handleChange}
                    inputProps={{ step: '10', min: '0' }}
                    helperText="Vorbelegt aus dem Kundenstamm"
                  />
                  {formData.doc_variant !== 'sketch' && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.attach_terms !== false}
                        onChange={(e) => setFormData(prev => ({ ...prev, attach_terms: e.target.checked }))}
                      />
                    }
                    label="Angebotsbedingungen anhängen"
                  />
                  )}
                </Box>
              </>
            )}

            {formData.invoice_type === 'invoice' && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <TextField
                  fullWidth
                  margin="normal"
                  label="Skonto (%)"
                  name="discount_rate"
                  type="number"
                  inputProps={{ step: '0.01', min: '0', max: '100' }}
                  value={formData.discount_rate}
                  onChange={handleChange}
                  helperText="z.B. 3 für 3% Skonto"
                />

                <TextField
                  select
                  fullWidth
                  margin="normal"
                  label="Skonto-Zeitraum (Tage)"
                  name="discount_days"
                  value={formData.discount_days}
                  onChange={handleChange}
                >
                  <MenuItem value="0">Kein Skonto</MenuItem>
                  <MenuItem value="7">7 Tage</MenuItem>
                  <MenuItem value="10">10 Tage</MenuItem>
                  <MenuItem value="14">14 Tage</MenuItem>
                </TextField>
              </Box>
            )}

            <TextField
              fullWidth
              margin="normal"
              label="Einleitungstext (optional)"
              name="intro_text"
              value={formData.intro_text}
              onChange={handleChange}
              multiline
              rows={2}
              placeholder="z.B. wir bedanken uns für den Auftrag..."
            />

            <TextField
              fullWidth
              margin="normal"
              label="Notizen (optional)"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeModal}
            sx={{
              color: '#1e3a5f',
              fontWeight: 600,
            }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="invoice-form"
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            {editingId ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Items View/Edit Dialog */}
      <Dialog
        open={showItemsDialog}
        onClose={closeItemsDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
            Rechnungspositionen
          </Box>
          <IconButton onClick={closeItemsDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedInvoice && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Rechnung:</strong> {selectedInvoice.invoice_number}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Kunde:</strong> {getCustomerName(selectedInvoice.customer_id)}
                  </Typography>
                </Box>
                {['draft', 'created', 'sent', 'paid'].includes(selectedInvoice.status) && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                    sx={{
                      background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
                      color: 'white',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
                      },
                    }}
                  >
                    Position hinzufügen
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {items.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    Keine Positionen vorhanden
                  </Typography>
                  {['draft', 'created', 'sent', 'paid'].includes(selectedInvoice.status) && (
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddItem}
                      sx={{ mt: 2 }}
                    >
                      Erste Position hinzufügen
                    </Button>
                  )}
                </Box>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          {['draft', 'created', 'sent', 'paid'].includes(selectedInvoice.status) && (
                            <TableCell sx={{ fontWeight: 600, width: 40 }}></TableCell>
                          )}
                          <TableCell sx={{ fontWeight: 600 }}>Pos.</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Beschreibung</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Menge</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Einzelpreis</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Gesamtpreis</TableCell>
                          {['draft', 'created', 'sent', 'paid'].includes(selectedInvoice.status) && (
                            <TableCell align="center" sx={{ fontWeight: 600 }}>Aktionen</TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <SortableContext
                        items={items.map(item => item.id)}
                        strategy={verticalListSortingStrategy}
                      >
                        <TableBody>
                          {items.map((item) => (
                            <SortableItemRow
                              key={item.id}
                              item={item}
                              selectedInvoice={selectedInvoice}
                              formatGerman={formatGerman}
                              handleEditItem={handleEditItem}
                              handleDeleteItem={handleDeleteItem}
                            />
                          ))}
                        </TableBody>
                      </SortableContext>
                    </Table>
                  </TableContainer>
                </DndContext>
              )}

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Typography variant="body1">
                  <strong>Netto:</strong> {formatGerman(selectedInvoice.total_net)} €
                </Typography>
                <Typography variant="body1">
                  <strong>MwSt ({parseFloat(selectedInvoice.tax_rate).toFixed(0)}%):</strong> {formatGerman(selectedInvoice.tax_amount)} €
                </Typography>
                <Typography variant="h6" color="primary" fontWeight={700}>
                  <strong>Brutto:</strong> {formatGerman(selectedInvoice.total_gross)} €
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={closeItemsDialog}
            sx={{
              color: '#1e3a5f',
              fontWeight: 600,
            }}
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Item Dialog */}
      <Dialog
        open={showAddItemDialog}
        onClose={() => setShowAddItemDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, minHeight: '70vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
            {editingItemId ? 'Position bearbeiten' : 'Position hinzufügen'}
          </Box>
          <IconButton onClick={() => setShowAddItemDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box component="form" id="item-form" onSubmit={handleItemSubmit} sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <TextField
              fullWidth
              margin="normal"
              label="Art.Nr. / Task-ID"
              name="article_number"
              value={itemFormData.article_number}
              onChange={handleItemChange}
              helperText="Optionale Artikelnummer oder externe Task-ID"
            />

            <Box sx={{ mt: 2, mb: 1, flex: 1, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                Beschreibung *
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  '& .ck-editor': {
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                  },
                  '& .ck-editor__main': {
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  },
                  '& .ck-editor__editable': {
                    minHeight: '250px',
                    flex: 1,
                    '& ul': {
                      listStyleType: 'disc',
                      marginLeft: '20px',
                      paddingLeft: '4px',
                    },
                    '& ol': {
                      listStyleType: 'decimal',
                      marginLeft: '20px',
                      paddingLeft: '4px',
                    },
                    '& li': {
                      display: 'list-item',
                    },
                    '& blockquote': {
                      borderLeft: '3px solid #ccc',
                      paddingLeft: '12px',
                      fontStyle: 'italic',
                      color: '#666',
                      margin: '8px 0',
                    },
                  },
                  '& .ck.ck-editor__editable:not(.ck-editor__nested-editable).ck-focused': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                  },
                }}
              >
                <StableCKEditor
                  editor={ClassicEditor}
                  data={toEditorHtml(itemFormData.description || '')}
                  config={{
                    placeholder: 'Beschreibung der Position...',
                    toolbar: [
                      'heading', '|',
                      'bold', 'italic', '|',
                      'bulletedList', 'numberedList', '|',
                      'blockQuote', 'insertTable', '|',
                      'undo', 'redo'
                    ],
                  }}
                  onChange={(_, editor) => {
                    const data = editor.getData();
                    setItemFormData(prev => ({
                      ...prev,
                      description: data,
                    }));
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField
                fullWidth
                margin="normal"
                label="Menge"
                name="quantity"
                type="number"
                inputProps={{ step: '0.01' }}
                value={itemFormData.quantity}
                onChange={handleItemChange}
                required
              />

              <TextField
                select
                fullWidth
                margin="normal"
                label="Einheit"
                name="unit"
                value={itemFormData.unit}
                onChange={handleItemChange}
                required
              >
                <MenuItem value="Stunden">Stunden</MenuItem>
                <MenuItem value="PT">PT (Personentage)</MenuItem>
                <MenuItem value="Stück">Stück</MenuItem>
                <MenuItem value="Pauschal">Pauschal</MenuItem>
              </TextField>
            </Box>

            <TextField
              fullWidth
              margin="normal"
              label="Einzelpreis (€)"
              name="unit_price"
              type="number"
              inputProps={{ step: '0.01' }}
              value={itemFormData.unit_price}
              onChange={handleItemChange}
              required
            />

            {itemFormData.quantity && itemFormData.unit_price && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <strong>Gesamtpreis:</strong> {formatGerman(parseFloat(itemFormData.quantity) * parseFloat(itemFormData.unit_price))} €
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setShowAddItemDialog(false)}
            sx={{
              color: '#1e3a5f',
              fontWeight: 600,
            }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="item-form"
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            {editingItemId ? 'Aktualisieren' : 'Hinzufügen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bezahlt-Datum Dialog */}
      <Dialog
        open={showPaidDialog}
        onClose={() => setShowPaidDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rechnung als bezahlt markieren</DialogTitle>
        <DialogContent>
          <TextField
            label="Bezahlt am"
            type="date"
            value={paidDate}
            onChange={(e) => setPaidDate(e.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaidDialog(false)}>Abbrechen</Button>
          <Button onClick={handleConfirmPaid} variant="contained" color="success">
            Als bezahlt markieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Preview/Edit Dialog */}
      <Dialog
        open={showEmailDialog}
        onClose={() => setShowEmailDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
            E-Mail versenden
          </Box>
          <IconButton onClick={() => setShowEmailDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" id="email-form" onSubmit={handleEmailSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="Empfänger"
              name="recipient"
              type="email"
              value={emailFormData.recipient}
              onChange={handleEmailChange}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="CC (Kopie)"
              name="cc"
              type="email"
              value={emailFormData.cc}
              onChange={handleEmailChange}
              helperText="Optionale Kopie-Empfänger (aus Kundenprofil)"
            />

            <TextField
              fullWidth
              margin="normal"
              label="Betreff"
              name="subject"
              value={emailFormData.subject}
              onChange={handleEmailChange}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Nachricht"
              name="body"
              value={emailFormData.body}
              onChange={handleEmailChange}
              multiline
              rows={8}
              required
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />

            {selectedInvoice && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Anhänge: {selectedInvoice.doc_label || (selectedInvoice.invoice_type === 'quote' ? 'Angebot' : 'Rechnung')} (PDF)
                {selectedInvoice.has_timesheet && ' + Zeitnachweis (PDF)'}
              </Typography>
            )}

            <TextField
              fullWidth
              margin="normal"
              label="Fußzeile / Signatur"
              name="footer"
              value={emailFormData.footer}
              onChange={handleEmailChange}
              multiline
              rows={8}
              required
              sx={{
                '& .MuiInputBase-input': {
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setShowEmailDialog(false)}
            sx={{
              color: '#1e3a5f',
              fontWeight: 600,
            }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            form="email-form"
            variant="contained"
            startIcon={<EmailIcon />}
            sx={{
              background: 'linear-gradient(135deg, #e01e26 0%, #ff4449 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': {
                background: 'linear-gradient(135deg, #c01820 0%, #e03840 100%)',
              },
            }}
          >
            E-Mail senden
          </Button>
        </DialogActions>
      </Dialog>
      <TimesheetDialog
        invoice={timesheetInvoice}
        projects={projects}
        onClose={() => setTimesheetInvoice(null)}
        onChanged={(updated) => setInvoices((prev) => prev.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)))}
        onPreview={(updated, tab) => setPreview({ invoice: updated, tab })}
      />
      <InvoicePreviewDialog
        invoice={preview?.invoice}
        initialTab={preview?.tab}
        onClose={() => setPreview(null)}
      />
    </Container>
  );
};

export default InvoicesPage;
